import { countdownParts, type Countdown as CountdownData } from '@content-scheduler/shared/render';
import { useEffect, useState } from 'react';
import styles from './parts.module.css';

const pad = (n: number) => String(n).padStart(2, '0');

function useNow(until: Date | null) {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (!until || now >= until.getTime()) return;
        const id = window.setTimeout(() => setNow(Date.now()), 1000 - (Date.now() % 1000));
        return () => window.clearTimeout(id);
    }, [now, until]);
    return now;
}

type Props = {
    countdown: CountdownData;
    target: Date | null;
    /** A single inline chip, for the slim bar layouts. */
    compact?: boolean;
    className?: string;
};

export function Countdown({ countdown, target, compact, className }: Props) {
    const now = useNow(target);
    if (!target) return null;
    const remaining = target.getTime() - now;
    if (remaining <= 0 && countdown.onExpire === 'hide') return null;

    const { days, hours, minutes, seconds } = countdownParts(remaining);
    const label = countdown.label.trim();
    // Screen readers get the deadline once, not a number changing every second.
    const deadline = (
        <span className={styles.visuallyHidden}>
            {target.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
        </span>
    );

    if (compact) {
        const text = `${days > 0 ? `${days}d ` : ''}${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
        return (
            <span className={[styles.countdownCompact, className].filter(Boolean).join(' ')}>
                {label && <span className={styles.countdownLabel}>{label}</span>}
                <time dateTime={target.toISOString()} aria-hidden="true">
                    {text}
                </time>
                {deadline}
            </span>
        );
    }

    const segments: [number, string][] = [
        ...(days > 0 ? ([[days, days === 1 ? 'day' : 'days']] as [number, string][]) : []),
        [hours, 'hrs'],
        [minutes, 'min'],
        [seconds, 'sec'],
    ];

    return (
        <div className={[styles.countdown, className].filter(Boolean).join(' ')} role="timer">
            {label && <span className={styles.countdownLabel}>{label}</span>}
            <div className={styles.segments} aria-hidden="true">
                {segments.map(([value, unit]) => (
                    <span key={unit} className={styles.segment}>
                        <span className={styles.segmentValue}>{pad(value)}</span>
                        <span className={styles.segmentUnit}>{unit}</span>
                    </span>
                ))}
            </div>
            {deadline}
        </div>
    );
}
